import { Button, Typography, Tag, Space, Badge } from "antd";
import { EditOutlined, DeleteOutlined, PictureOutlined } from "@ant-design/icons";
import { useMemo } from "react";

import Table from "../../../shared/components/ui/Table";
import { TableColumns } from "../../../shared/components/ui/Table/types";
import { productsService } from "../../../shared/services";
import { useProducts, useTableProduct } from "../hooks";
import { PATH_S3_BUCKET } from "../../../shared/utils/constants";
import { IProducts } from "../../../redux/@types";
import { decimal } from "../../../shared/utils/Helper";

const { Text } = Typography;

interface Props {
  fetchQuery?: any;
}

export const TableProducts = ({ fetchQuery }: Props) => {
  const { showDeleteConfirm, changeProductsForUpdate } = useTableProduct();
  const { setTableProps, updateStatus, isLoading } = useProducts();
  const columns: TableColumns<IProducts> = [
    {
      title: "Producto",
      width: 15,
      key: "product_info",
      render: (_, item) => {
        return (
          <Space size={12}>
            <div style={{ 
              width: 48, 
              height: 48, 
              borderRadius: 8, 
              overflow: 'hidden', 
              border: "1px solid #f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fafafa"
            }}>
              {item.media && item.media[0] ? (
                <img
                  src={`${PATH_S3_BUCKET}/${item.media.sort((a, b) => b.priority - a.priority)[0]?.path!}`}
                  alt={item.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <PictureOutlined style={{ color: "#bfbfbf", fontSize: 20 }} />
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Text strong style={{ fontSize: 14, color: "#262626" }}>{item.name}</Text>
              <Space size={4} split={<Text type="secondary">|</Text>}>
                <Text type="secondary" style={{ fontSize: 12, fontFamily: "monospace" }}>{item.code}</Text>
                {item.category && (
                  <Tag color="blue" style={{ border: "none", margin: 0, fontSize: 10, lineHeight: "18px" }}>
                    {item.category.name}
                  </Tag>
                )}
              </Space>
            </div>
          </Space>
        );
      },
    },
    {
      title: "Precio",
      width: 8,
      dataIndex: "price_with_tax",
      key: "price",
      render: (_, item) => {
        const precioMostrar = item.discount_price_whit_tax ?? item.price_with_tax;
        return (
          <Text strong style={{ fontSize: 14, color: "#262626" }}>
            $ {decimal(precioMostrar)}
          </Text>
        );
      },
    },
    {
      title: "Stock",
      width: 8,
      dataIndex: "quantity",
      key: "quantity",
      render: (quantity) => (
        <Badge 
          status={quantity > 0 ? "success" : "error"} 
          text={<Text style={{ color: quantity > 0 ? "#262626" : "#ff4d4f" }}>{quantity} un.</Text>} 
        />
      ),
    },
    {
      title: "Estado",
      width: 8,
      dataIndex: "status",
      key: "status",
      render: (_, item) => {
        const isActive = item.status === "active";
        return (
          <Tag
            onClick={() => {
              if (!isLoading) {
                updateStatus(isActive ? "inactive" : "active", item.id);
              }
            }}
            style={{
              cursor: isLoading ? "not-allowed" : "pointer",
              padding: "2px 10px",
              borderRadius: "12px",
              border: `1px solid ${isActive ? "#b7eb8f" : "#ffa39e"}`,
              fontSize: "12px",
              fontWeight: 500,
              background: isActive ? "#f6ffed" : "#fff1f0",
              color: isActive ? "#389e0d" : "#cf1322",
              transition: "all 0.2s",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isActive ? "Activo" : "Inactivo"}
          </Tag>
        );
      },
      filters: [
        { text: "Activo", value: "active" },
        { text: "Inactivo", value: "inactive" },
      ],
    },
    {
      title: "Acciones",
      key: "operation",
      fixed: "right",
      width: 8,
      render: (_, item) => {
        return (
          <Space>
            <Button
              type="text"
              onClick={() => changeProductsForUpdate(item)}
              icon={<EditOutlined style={{ color: "#1890ff" }} />}
              style={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#e6f7ff",
                borderRadius: 6
              }}
            />
            <Button
              type="text"
              danger
              onClick={() => showDeleteConfirm(item.id, `${item.name}`)}
              icon={<DeleteOutlined />}
              style={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#fff1f0",
                borderRadius: 6
              }}
            />
          </Space>
        );
      },
    },
  ];

  const fetchQueryProps = useMemo(
    () => ({
      $sort: { id: -1 },
      $client: { skipProductPrice: true },
      ...fetchQuery,
    }),
    [fetchQuery]
  );

  return (
    <Table<IProducts>
      columns={columns}
      fetchQueryProps={fetchQueryProps}
      service={productsService}
      onLoad={setTableProps}
    />
  );
};
